export type ThemeType = "light" | "dark" | "system" | "pink" | "ice-blue";

export interface ThemeConfig {
  id: ThemeType;
  name: string;
  description: string;
  previewColors: {
    primary: string;
    background: string;
    surface: string;
    accent: string;
  };
  className: string;
}

export const themes: ThemeConfig[] = [
  {
    id: "light",
    name: "Light",
    description: "Clean and bright theme for daytime use",
    previewColors: {
      primary: "#2563eb",
      background: "#ffffff",
      surface: "#f8fafc",
      accent: "#3b82f6",
    },
    className: "theme-light",
  },
  {
    id: "dark",
    name: "Dark",
    description: "Easy on the eyes for nighttime use",
    previewColors: {
      primary: "#3b82f6",
      background: "#0a0a0a",
      surface: "#1a1a1a",
      accent: "#60a5fa",
    },
    className: "dark",
  },
  {
    id: "system",
    name: "System",
    description: "Automatically follows your device settings",
    previewColors: {
      primary: "#6366f1",
      background: "#f1f5f9",
      surface: "#e2e8f0",
      accent: "#8b5cf6",
    },
    className: "theme-system",
  },
  {
    id: "pink",
    name: "Pink",
    description: "Warm and energetic with rose tones",
    previewColors: {
      primary: "#e91e63",
      background: "#fdf2f8",
      surface: "#fce7f3",
      accent: "#f06292",
    },
    className: "theme-pink",
  },
  {
    id: "ice-blue",
    name: "Ice Blue",
    description: "Cool and professional blue theme",
    previewColors: {
      primary: "#0ea5e9",
      background: "#f8fafc",
      surface: "#f1f5f9",
      accent: "#38bdf8",
    },
    className: "theme-ice-blue",
  },
];

export const getThemeConfig = (themeId: ThemeType): ThemeConfig => {
  return themes.find((theme) => theme.id === themeId) || themes[0];
};

export const applyTheme = (themeId: ThemeType) => {
  const root = document.documentElement;

  // Remove all theme classes
  themes.forEach((theme) => {
    root.classList.remove(theme.className);
  });

  if (themeId === "system") {
    // For system theme, detect user preference
    const prefersDark = window.matchMedia(
      "(prefers-color-scheme: dark)",
    ).matches;
    root.classList.add(prefersDark ? "dark" : "theme-light");
  } else {
    const themeConfig = getThemeConfig(themeId);
    root.classList.add(themeConfig.className);
  }
};

// Listen for system theme changes
export const setupSystemThemeListener = (
  onThemeChange: (theme: ThemeType) => void,
) => {
  const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

  const handleChange = () => {
    // Only trigger if current theme is system - we track this differently
    onThemeChange("system");
  };

  mediaQuery.addEventListener("change", handleChange);

  return () => mediaQuery.removeEventListener("change", handleChange);
};
